import { Fragment, useEffect, useRef, useState } from "react";
import { ScanSearch } from "lucide-react";

const ENGLISH_WORD_PATTERN = /([A-Za-z]+(?:['’-][A-Za-z]+)*)/g;
const ENGLISH_WORD = /^[A-Za-z]+(?:['’-][A-Za-z]+)*$/;

function getSelectedText() {
  return window.getSelection?.()?.toString().replace(/\s+/g, " ").trim() ?? "";
}

function InteractiveSentence({
  text,
  sentenceId,
  targetWords,
  occurrencePrefix,
  checkedOccurrenceIds,
  selectedSentenceId,
  onWordClick,
}) {
  const parts = text.split(ENGLISH_WORD_PATTERN);

  return (
    <span
      className={
        selectedSentenceId === sentenceId
          ? "passage-sentence is-selected"
          : "passage-sentence"
      }
      data-passage-sentence
      data-sentence-id={sentenceId}
    >
      {parts.map((part, partIndex) => {
        if (!ENGLISH_WORD.test(part)) return part;

        const isTarget = targetWords.some(
          (word) => word.toLowerCase() === part.toLowerCase(),
        );
        const occurrenceId = `${occurrencePrefix}:token-${partIndex}`;
        const isChecked = checkedOccurrenceIds.has(occurrenceId);
        const openQuiz = () => {
          if (getSelectedText()) return;
          onWordClick(part, text, occurrenceId);
        };

        if (isTarget) {
          return (
            <button
              type="button"
              className={
                isChecked ? "word-highlight word-checked" : "word-highlight"
              }
              aria-label={`根据上下文猜测 ${part} 的意思`}
              key={`${part}-${partIndex}`}
              onClick={openQuiz}
            >
              {part}
            </button>
          );
        }

        return (
          <span
            className={isChecked ? "lookup-word word-checked" : "lookup-word"}
            data-lookup-word={part}
            key={`${part}-${partIndex}`}
            onPointerUp={openQuiz}
          >
            {part}
          </span>
        );
      })}
    </span>
  );
}

function PassageView({
  passage,
  generation,
  requestedLength,
  isGenerating,
  generationError,
  checkedOccurrenceIds,
  selectedSentence,
  onWordClick,
  onSentenceSelect,
  onOpenSentenceDetective,
  onClearSentenceSelection,
}) {
  const [selectionAction, setSelectionAction] = useState(null);
  const passageCopyRef = useRef(null);

  useEffect(() => {
    if (!selectedSentence) setSelectionAction(null);
  }, [selectedSentence]);

  useEffect(() => {
    if (!selectionAction) return undefined;

    const clearStaleSelection = () => {
      const selection = window.getSelection?.();
      const container = passageCopyRef.current;
      if (
        !selection ||
        selection.isCollapsed ||
        !container?.contains(selection.anchorNode) ||
        !container.contains(selection.focusNode)
      ) {
        setSelectionAction(null);
        onClearSentenceSelection();
      }
    };

    document.addEventListener("selectionchange", clearStaleSelection);
    return () =>
      document.removeEventListener("selectionchange", clearStaleSelection);
  }, [selectionAction, onClearSentenceSelection]);

  function captureSentenceSelection(event) {
    const selection = window.getSelection?.();
    setSelectionAction(null);
    onClearSentenceSelection();

    if (
      !selection ||
      selection.isCollapsed ||
      selection.rangeCount === 0
    ) {
      return;
    }

    const container = event.currentTarget;
    const selectionRange = selection.getRangeAt(0);
    const firstSentence = [
      ...container.querySelectorAll("[data-passage-sentence]"),
    ].find((sentence) => {
      try {
        return selectionRange.intersectsNode(sentence);
      } catch {
        return false;
      }
    });
    if (!firstSentence) return;

    const text = firstSentence.textContent.replace(/\s+/g, " ").trim();
    if (!text) return;

    const firstLineRect = firstSentence.getClientRects?.()[0];
    const rect =
      firstLineRect && (firstLineRect.width || firstLineRect.height)
        ? firstLineRect
        : firstSentence.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const actionWidth = 128;
    const spaceAbove = rect.top - containerRect.top;
    const top =
      spaceAbove >= 48
        ? spaceAbove - 46
        : rect.bottom - containerRect.top + 8;
    const left = Math.min(
      Math.max(
        rect.left -
          containerRect.left +
          rect.width / 2 -
          actionWidth / 2,
        0,
      ),
      Math.max(containerRect.width - actionWidth, 0),
    );

    setSelectionAction({
      sentenceId: firstSentence.dataset.sentenceId,
      top,
      left,
    });
    onSentenceSelect(text);
  }

  function openSentenceDetective() {
    window.getSelection?.()?.removeAllRanges();
    onOpenSentenceDetective();
  }

  return (
    <section className="reading-workspace" aria-label="生成的英文文章">
      <div className="reading-toolbar">
        <div>
          <span className="live-label">
            <span
              className={passage ? "status-dot" : "status-dot idle"}
              aria-hidden="true"
            />
            {isGenerating ? "生成中" : passage ? "已生成" : "待生成"}
          </span>
          <span className="toolbar-divider" aria-hidden="true" />
          <span>
            目标 {passage ? generation.passageLength : requestedLength} 词
          </span>
        </div>
      </div>

      {!passage && !isGenerating && !generationError ? (
        <div className="empty-passage">
          <span aria-hidden="true">Aa</span>
          <h2>暂无阅读材料</h2>
        </div>
      ) : (
        <article className={isGenerating ? "passage is-loading" : "passage"}>
          {isGenerating && (
            <div className="loading-state" role="status">
              <span className="loading-line long" />
              <span className="loading-line medium" />
              <span className="loading-line short" />
            </div>
          )}

          {generationError && (
            <div className="passage-error" role="alert">
              <strong>未生成新文章</strong>
              <span>{generationError}</span>
            </div>
          )}

          {passage && (
            <>
              <div className="article-heading">
                <p>{passage.eyebrow}</p>
                <h2>{passage.title}</h2>
                <div className="article-meta">
                  <span>Reading practice</span>
                  <span>{generation.words.length} 个目标词</span>
                  <span>
                    {generation.actualWordCount
                      ? `${generation.actualWordCount} 词`
                      : `约 ${generation.passageLength} 词`}
                  </span>
                </div>
              </div>

              <div
                className="passage-copy"
                ref={passageCopyRef}
                onPointerUp={captureSentenceSelection}
              >
                {passage.paragraphs.map((paragraph, paragraphIndex) => (
                  <p key={paragraphIndex}>
                    {paragraph.sentences.map((sentence, sentenceIndex) => {
                      const sentenceId =
                        `${passage.id ?? "passage"}:paragraph-` +
                        `${paragraphIndex}:sentence-${sentenceIndex}`;

                      return (
                        <Fragment key={sentenceId}>
                          <InteractiveSentence
                            text={sentence.text}
                            sentenceId={sentenceId}
                            targetWords={generation.words}
                            occurrencePrefix={sentenceId}
                            checkedOccurrenceIds={checkedOccurrenceIds}
                            selectedSentenceId={selectionAction?.sentenceId}
                            onWordClick={onWordClick}
                          />
                          {sentenceIndex < paragraph.sentences.length - 1
                            ? " "
                            : null}
                        </Fragment>
                      );
                    })}
                  </p>
                ))}
                {selectedSentence && selectionAction && (
                  <button
                    type="button"
                    className="sentence-detective-float"
                    style={{
                      top: selectionAction.top,
                      left: selectionAction.left,
                    }}
                    onPointerDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onPointerUp={(event) => event.stopPropagation()}
                    onClick={openSentenceDetective}
                  >
                    <ScanSearch aria-hidden="true" size={18} strokeWidth={2} />
                    <span>句子侦探</span>
                  </button>
                )}
              </div>

              <footer className="vocabulary-footer">
                <div className="footer-heading">
                  <strong>本篇目标词</strong>
                  <span>文中已高亮</span>
                </div>
                <div className="word-chips">
                  {generation.words.map((word, index) => (
                    <span key={word}>
                      <small>{String(index + 1).padStart(2, "0")}</small>
                      {word}
                    </span>
                  ))}
                </div>
              </footer>
            </>
          )}
        </article>
      )}
    </section>
  );
}

export default PassageView;
